import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import CollaboratoreDialog from "./CollaboratoreDialog";
import PraticaSummaryChips from "./PraticaSummaryChips";
import NotePreview from "./NotePreview";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pencil } from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const sortTok = (s) => norm(s).split(" ").filter(Boolean).sort().join(" ");
const collabName = (c) => [c.cognome, c.nome].filter(Boolean).join(" ").trim();

// Campo pratica: mostra i collaboratori del professionista selezionato (se presenti)
// e consente di inserirne uno nuovo al volo. Non renderizza nulla se il professionista
// non ha collaboratori censiti.
export default function CollaboratoreField({ atype, professionista, value, onChange, testId, label }) {
  const [rec, setRec] = useState(null);
  const [dialog, setDialog] = useState(null); // { editIndex }

  const resolve = useCallback(async () => {
    const nome = (professionista || "").trim();
    if (!nome) { setRec(null); return; }
    try {
      // Carico l'elenco senza filtro q: il backend cerca per singolo campo, quindi
      // un nome completo "Mario Rossi" non matcherebbe. Filtro lato client sul nominativo.
      const { data } = await api.get(`/anagrafiche/${atype}`);
      const target = norm(nome), ts = sortTok(nome);
      const found = (data || []).find((r) => {
        const dn = norm(r.denominazione || [r.nome, r.cognome].filter(Boolean).join(" "));
        const cn = norm([r.cognome, r.nome].filter(Boolean).join(" "));
        return dn === target || cn === target || sortTok([r.nome, r.cognome].filter(Boolean).join(" ")) === ts;
      });
      setRec(found || null);
    } catch { setRec(null); }
  }, [atype, professionista]);

  useEffect(() => { resolve(); }, [resolve]);

  const collaboratori = Array.isArray(rec?.collaboratori) ? rec.collaboratori : [];
  // Mostra il campo appena il professionista selezionato esiste in anagrafica:
  // se ha collaboratori li elenca, altrimenti resta comunque disponibile "+ Nuovo".
  if (!rec) return null;

  const names = collaboratori.map(collabName).filter(Boolean);
  const extra = value && !names.includes(value) ? [value] : [];
  const selIdx = value ? collaboratori.findIndex((c) => collabName(c) === value) : -1;
  const sel = selIdx >= 0 ? collaboratori[selIdx] : null;
  const vals = (k) => (Array.isArray(sel?.[k]) ? sel[k] : (sel?.[k] ? [sel[k]] : [])).map((v) => (v || "").trim()).filter(Boolean);
  const contactItems = sel ? ["telefono", "cellulare", "email", "pec"].flatMap((k) => vals(k).map((v) => ({ key: k, value: v }))) : [];
  const cellLbls = Array.isArray(sel?.cellulare_etichette) ? sel.cellulare_etichette : [];
  const waNumbers = (Array.isArray(sel?.cellulare) ? sel.cellulare : (sel?.cellulare ? [sel.cellulare] : []))
    .map((n, i) => ({ n: (n || "").trim(), label: String(cellLbls[i] || "").trim() })).filter((o) => o.n);

  return (
    <div className="grid grid-cols-[40%_60%] border-b border-slate-300 last:border-0">
      <div className="font-bold uppercase text-[11px] tracking-wide px-3 py-2 leading-snug flex items-center bg-slate-600 text-white" data-testid={`${testId}-label`}>{atype === "periti" ? "Collab. perito comp." : "Collab. studio"}{label ? ` ${label}` : ""}</div>
      <div className="px-3 py-2 bg-white [&_[role=combobox]]:bg-white [&_[role=combobox]]:text-slate-900 [&_[role=combobox]]:border-slate-300">
        <div className="flex items-center gap-1.5">
        <Select value={value || "__none__"} onValueChange={(v) => { if (v === "__new__") { setDialog({ editIndex: null }); return; } onChange(v === "__none__" ? "" : v); }}>
          <SelectTrigger data-testid={testId} className="flex-1 h-9"><SelectValue placeholder="Seleziona collaboratore" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-64">
            <SelectItem value="__none__">Nessuno</SelectItem>
            {extra.map((n) => <SelectItem key={`x-${n}`} value={n}>{n}</SelectItem>)}
            {collaboratori.map((c, idx) => { const n = collabName(c); return n ? <SelectItem key={idx} value={n}>{n}</SelectItem> : null; })}
            <SelectItem value="__new__" data-testid={`${testId}-crea`} className="text-teal-300 font-medium border-t border-slate-700">+ Crea nuovo collaboratore</SelectItem>
          </SelectContent>
        </Select>
        <button type="button" disabled={selIdx < 0} onClick={() => setDialog({ editIndex: selIdx })} data-testid={`${testId}-edit`} title={selIdx < 0 ? "Nessun collaboratore selezionato" : "Modifica collaboratore"}
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-teal-500/40 text-teal-600 hover:bg-teal-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"><Pencil className="h-4 w-4" /></button>
        <NotePreview note={sel?.note} testId={`${testId}-note-preview`} className="" />
        </div>
        {contactItems.length > 0 && <PraticaSummaryChips items={contactItems} waNumbers={waNumbers} testId={testId} waCategory="collaboratore_professionista" waName={value} />}
      </div>
      {dialog && (
        <CollaboratoreDialog open atype={atype} proId={rec.id} proName={professionista} existing={collaboratori} editIndex={dialog.editIndex}
          onCancel={() => setDialog(null)}
          onSaved={async (collab) => { setDialog(null); await resolve(); onChange(collabName(collab)); }} />
      )}
    </div>
  );
}
